<template>
  <div class="flex flex-col p-3">
    <!-- Filter bar -->
    <div class="flex gap-3 py-3">
      <input
        type="text"
        v-model="trackingNumber"
        class="w-48 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
        placeholder="Search tracking no"
        @keyup.enter="fetch"
      />
      <!-- <input
        type="text"
        v-model="prn"
        class="w-48 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
        placeholder="Search PRN"
        @keyup.enter="fetch"
      /> -->
      <button @click="fetch" class="btn-sm-primary py-2">
        <i class="fa fa-search mr-1"></i> Search
      </button>
      <button v-if="trackingNumber || prn" @click="clearSearch" class="btn-sm-outline border border-gray-300 text-gray-600 hover:bg-gray-100 py-2 px-2 ml-2">
        <i class="fa fa-times mr-1"></i> Clear
      </button>
    </div>

    <!-- Table -->
    <div>
      <table>
        <thead>
        <tr>
          <th>#</th>
          <th>Tracking Number</th>
          <th>Total Amount</th>
          <th>Balance</th>
          <th>Status</th>
          <th>Confirmed</th>
          <th>Date</th>
          <th>Action</th>
        </tr>
        </thead>
        <tbody>
        <template v-if="loading">
          <tr v-for="i in 6" :key="`sk-${i}`" class="animate-pulse">
            <td v-for="j in 8" :key="j">
              <div class="h-3 bg-gray-200 rounded"></div>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr v-for="(invoice, idx) in invoices" :key="idx">
            <td>{{ (currentPage - 1) * limit + idx + 1 }}</td>
            <td class="font-bold font-mono text-primary">{{ invoice.trackingNumber || invoice.trackingNo }}</td>
            <td class="font-medium text-gray-700">UGX {{ invoice.total?.toLocaleString() || invoice.totalAmount || 0 }}</td>
            <td class="font-medium text-gray-700">UGX {{ invoice.balance?.toLocaleString() || invoice.balance || 0 }}</td>
            <td>
              <span v-if="invoice.status" :class="statusPill(invoice.status)">
                {{ invoice.status.toLowerCase() }}
              </span>
              <span v-else>-</span>
            </td>
            <td>
              <span v-if="invoice.confirmed" class="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700">Yes</span>
              <span v-else class="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">No</span>
            </td>
            <td>{{ dateFormat(invoice.createdAt || invoice.createdDate || invoice.date) }}</td>
            <td>
              <button class="btn-sm-primary" @click="view(invoice)">
                <i class="fa fa-eye mr-1"></i> View
              </button>
            </td>
          </tr>
        </template>
        <tr v-if="!loading && invoices.length === 0">
          <td colspan="8" class="text-center py-6 text-gray-400 text-sm">No invoices found</td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.totalPages > 1" class="flex items-center justify-between mt-4 px-1">
      <div class="flex items-center gap-3">
        <button
          class="btn-sm-primary"
          :disabled="currentPage <= 1"
          @click="previous"
        >
          <i class="fa fa-chevron-left mr-1"></i> Previous
        </button>
        <span class="text-xs text-gray-500">Page {{ currentPage }} of {{ pagination.totalPages }}</span>
        <button
          class="btn-sm-primary"
          :disabled="currentPage >= pagination.totalPages"
          @click="next"
        >
          Next <i class="fa fa-chevron-right ml-1"></i>
        </button>
      </div>
      <div class="flex items-center gap-2">
        <select v-model="limit"
                class="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                @change="fetch">
          <option :value="12">12</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
        </select>
        <span class="text-xs text-gray-500">
          {{ currentPage === 1 ? 1 : (currentPage - 1) * limit + 1 }} – {{
            Math.min(currentPage * limit, pagination.totalElements)
          }} of {{ pagination.totalElements }}
        </span>
      </div>
    </div>

    <!-- Detail Modal -->
    <AppModal v-model="showDetails" size="xl" title="Invoice Details" hide-footer>
      <div v-if="loadingDetails" class="text-center py-10">
        <i class="fa fa-spinner fa-spin text-primary text-2xl"></i>
      </div>
      <div v-else-if="selectedInvoice" class="space-y-4 text-sm">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <p><b>Tracking Number:</b> {{ selectedInvoice.trackingNumber || selectedInvoice.trackingNo }}</p>
          <p><b>Type:</b> {{ selectedInvoice.type }}</p>
          <p><b>Application Type:</b> {{ selectedInvoice.applicationType }}</p>
          <p><b>Status:</b> <span :class="statusPill(selectedInvoice.status)">{{ selectedInvoice.status?.toLowerCase() }}</span></p>
          <p><b>Invoice Status:</b> {{ selectedInvoice.invoiceApplicationStatus }}</p>
          <p><b>Total Amount:</b> UGX {{ selectedInvoice.total?.parsedValue?.toLocaleString() ?? selectedInvoice.total?.toLocaleString() ?? 0 }}</p>
          <p><b>Amount Paid:</b> UGX {{ selectedInvoice.paid?.parsedValue?.toLocaleString() ?? selectedInvoice.paid?.toLocaleString() ?? 0 }}</p>
          <p><b>Balance:</b> UGX {{ selectedInvoice.balance?.parsedValue?.toLocaleString() ?? selectedInvoice.balance?.toLocaleString() ?? 0 }}</p>
          <p><b>Confirmed:</b> {{ selectedInvoice.confirmed ? 'Yes' : 'No' }}</p>
          <p><b>Date:</b> {{ dateFormat(selectedInvoice.date || selectedInvoice.createdAt) }}</p>
        </div>

        <div v-if="selectedInvoice.costBreakdown && Object.keys(selectedInvoice.costBreakdown).length > 0" class="mt-4">
          <p class="font-bold text-gray-700 capitalize border-b pb-1 mb-2">Cost Breakdown</p>
          <div class="mt-2 border border-gray-200 rounded-md overflow-hidden">
            <table class="w-full text-left border-collapse">
              <thead class="bg-gray-50 text-gray-700">
                <tr>
                  <th class="p-2 border-b text-xs font-semibold">Code</th>
                  <th class="p-2 border-b text-xs font-semibold">Description</th>
                  <th class="p-2 border-b text-xs font-semibold">Amount BeforeExchange Rate (UGX)</th>
                  <th class="p-2 border-b text-xs font-semibold text-right">Amount (UGX)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in (Object.values(selectedInvoice.costBreakdown) as any[])" :key="i" class="border-b last:border-0 hover:bg-gray-50">
                  <td class="p-2 text-xs">{{ item.code }}</td>
                  <td class="p-2 text-xs">{{ item.description }}</td>
                  <td class="p-2 text-xs  font-medium">{{ item.amountBeforeExchangeRate.toLocaleString() }}</td>
                  <td class="p-2 text-xs text-right font-medium">{{ item.amount?.parsedValue?.toLocaleString() ?? item.amount?.toLocaleString() ?? 0 }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppModal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { api } from "@/app/services/api.service";
import AppModal from "@/components/ui/AppModal.vue";
import { dateFormat } from "@/commons/composables/utilities.ts";
import { useToast } from "@/composables";

const { errorToast } = useToast();

const trackingNumber = ref("");
const prn = ref("");
const invoices = ref<any[]>([]);
const loading = ref(false);
const currentPage = ref(1);
const limit = ref(12);
const pagination = ref({ totalElements: 0, totalPages: 0 });

const showDetails = ref(false);
const loadingDetails = ref(false);
const selectedInvoice = ref<any>(null);

const statusPill = (status: string) => {
  const base = "text-xs px-2 py-0.5 rounded-full capitalize ";
  if (status === "CREDITED" || status === "COMPLETED" || status === "SUCCESS") return base + "bg-green-50 text-green-700";
  if (status === "FAILED") return base + "bg-red-50 text-red-700";
  if (status === "PENDING") return base + "bg-gray-100 text-gray-600";
  return base + "bg-gray-100 text-gray-600";
};

const fetch = async () => {
  loading.value = true;
  try {
    let url = `/incorporation/invoices?page=${currentPage.value}&size=${limit.value}`;
    if (trackingNumber.value) url += `&trackingNo=${trackingNumber.value}`;
    if (prn.value) url += `&prn=${prn.value}`;
    
    const response = await api().get(url);
    let data = response.data?.data || response.data?.content || response.data;
    
    if (data?.content && Array.isArray(data.content)) {
      invoices.value = data.content;
    } else if (Array.isArray(data)) {
      invoices.value = data;
    } else if (data && typeof data === 'object') {
      if ("0" in data || "1" in data) {
        invoices.value = Object.values(data);
      } else {
        invoices.value = [data];
      }
    } else {
      invoices.value = [];
    }
    
    pagination.value = {
      totalElements: response.data?.totalElements || response.data?.data?.totalElements || invoices.value.length,
      totalPages: response.data?.totalPages || response.data?.data?.totalPages || 1
    };
  } catch (error) {
    console.error("Failed to fetch invoices", error);
    errorToast("Error", "Failed to load invoices");
  } finally {
    loading.value = false;
  }
};

const view = (invoice: any) => {
  selectedInvoice.value = invoice;
  showDetails.value = true;
};

const next = () => {
  if (currentPage.value < pagination.value.totalPages) {
    currentPage.value++;
    fetch();
  }
};

const clearSearch = () => {
  trackingNumber.value = "";
  prn.value = "";
  currentPage.value = 1;
  fetch();
};

const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetch();
  }
};

onMounted(() => fetch());
</script>
